import React, { useState } from 'react';
import { Table as AntTable, Button as AntButton, Space, Typography as AntTypography } from 'antd';
import type { TableProps as AntTableProps } from 'antd';
import type { ColumnType as AntColumnType } from 'antd/es/table/interface';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Interface for column definition, adapted for Ant Design
export interface AntDColumn<T> extends AntColumnType<T> {
  id: string; // Keep id for mapping, but AntD uses `key` or `dataIndex` for actual column identification
  label: string; // Used for title in AntD column
  width?: number | string; // Explicitly add width
  align?: 'left' | 'right' | 'center'; // Explicitly add align
  render?: (value: any, record: T, index: number) => React.ReactNode; // Explicitly add render
  // minWidth, maxWidth, width can be set via AntD column `width` prop
  // align is available in AntD column
  // render is a standard part of AntD ColumnType<T>
}

// Interface for DataTable props
export interface DataTableProps<T extends object> { // T must be an object for AntD Table records
  columns: AntDColumn<T>[];
  data: T[];
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onView?: (item: T) => void;
  onBulkDelete?: (ids: React.Key[]) => void; // AntD rowSelection uses React.Key[]
  onBulkUpload?: () => void;
  getRowId: (row: T) => React.Key; // AntD uses React.Key for rowKey
  headerActions?: React.ReactNode;
  // stickyFirstDataColumn is handled by AntD Table's `fixed` prop on columns
  loading?: boolean;
  scroll?: AntTableProps<T>['scroll']; // Added scroll prop
  pagination?: any;
  onChange?: (pagination: any) => void;
  showViewAction?: boolean;
}

export const DataTable = <T extends object>({
  columns,
  data,
  onEdit,
  onDelete,
  onView,
  onBulkDelete,
  onBulkUpload,
  getRowId,
  headerActions,
  loading,
  scroll, // Added scroll here
  pagination,
  onChange,
  showViewAction = true,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const antdColumns: AntColumnType<T>[] = columns.map(col => ({
    key: col.id, // Use id as key
    dataIndex: col.id, // Assume id can be used as dataIndex; adjust if complex objects
    title: col.label,
    width: col.width,
    align: col.align as 'right' | 'left' | 'center', // Cast to AntD align types
    render: col.render,
    // To make a column sticky (like stickyFirstDataColumn):
    // if (col.id === columns[0]?.id && stickyFirstDataColumn) {
    //   (acc[0] as AntColumnType<T>).fixed = 'left';
    // }
  }));

  // Add actions column only if at least one action is present
  const hasActions = (onEdit || onDelete || (onView && showViewAction !== false));
  if (hasActions) {
    antdColumns.push({
      title: t('common:actions', 'Actions'),
      key: 'actions',
      align: 'right',
      width: 120,
      render: (_: any, record: T) => (
        <Space size="middle">
          {onView && showViewAction !== false && (
            <AntButton type="text" icon={<EyeOutlined />} onClick={() => onView(record)} />
          )}
          {onEdit && (
            <AntButton type="text" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          )}
          {onDelete && (
            <AntButton type="text" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
          )}
        </Space>
      ),
    });
  }

  const handleSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectChange,
  };

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <div style={{ width: '100%' }}> {/* Replaced Paper with div */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {hasSelected && onBulkDelete && (
            <Space>
              <AntTypography.Text>
                {t('common.selectedNItems', { count: selectedRowKeys.length })}
              </AntTypography.Text>
              <AntButton
                danger
                onClick={() => onBulkDelete(selectedRowKeys)}
                icon={<DeleteOutlined />}
              >
                {t('common.bulkDelete', 'Delete Selected')}
              </AntButton>
            </Space>
          )}
        </div>
        <Space>
          {headerActions}
          {onBulkUpload && (
            <AntButton onClick={onBulkUpload} icon={<UploadOutlined />}>
              {t('common.bulkUpload', 'Bulk Upload')}
            </AntButton>
          )}
        </Space>
      </div>
      <AntTable<T>
        rowKey={getRowId} // Use getRowId for rowKey
        columns={antdColumns}
        dataSource={data}
        rowSelection={onBulkDelete ? rowSelection : undefined} // Conditionally enable row selection
        pagination={pagination}
        scroll={scroll ?? { x: 'max-content' }} // Use passed scroll or default
        loading={loading}
        bordered
        size="small"
        onChange={onChange}
        onRow={(record) => ({
          onClick: () => onView?.(record),
          style: { cursor: onView ? 'pointer' : 'default' }
        })}
      />
    </div>
  );
};

// Note: stickyFirstDataColumn logic needs to be implemented by setting `fixed: 'left'` 
// on the first data column definition if that feature is still desired.
// Similarly for sticky action columns, `fixed: 'right'`. 